import {
  AIR_ACCEL,
  AIR_MAX_SPEED,
  Button,
  COYOTE_TIME_MS,
  CROUCH_MAX_SPEED,
  GRAVITY,
  GROUND_ACCEL,
  GROUND_FRICTION,
  GROUND_MAX_SPEED,
  JUMP_BUFFER_MS,
  JUMP_SPEED,
  MAX_PITCH,
  PLAYER_CROUCH_HEIGHT,
  PLAYER_HEIGHT,
  PLAYER_RADIUS,
  SLIDE_AIR_GRACE_MS,
  SLIDE_COOLDOWN_MS,
  SLIDE_DURATION_MS,
  SLIDE_FRICTION,
  SLIDE_IMPULSE,
  SLIDE_MAX_LAUNCH,
  SLIDE_MIN_SPEED,
  SPRINT_MAX_SPEED,
  STEP_HEIGHT,
  clamp,
  hasButton,
  yawBasis,
  type InputCommand,
  type PlayerState,
  type Vec3,
} from '@game/shared';
import { aabbOverlaps, type Aabb, type CollisionWorld } from './collision.js';

/** Skin width keeps the player from resting exactly on a face and jittering. */
const EPSILON = 1e-3;
/** Movement is split so no substep exceeds this distance on any axis. */
const MAX_SUBSTEP = 0.25;

const scratchAabb: Aabb = {
  minX: 0,
  minY: 0,
  minZ: 0,
  maxX: 0,
  maxY: 0,
  maxZ: 0,
};
const scratchIndices: number[] = [];

export const playerHeight = (
  player: Pick<PlayerState, 'crouching' | 'sliding'>,
): number =>
  player.crouching || player.sliding ? PLAYER_CROUCH_HEIGHT : PLAYER_HEIGHT;

/** Player bounds, given feet position. */
export const playerAabb = (position: Vec3, height: number, out: Aabb): Aabb => {
  out.minX = position.x - PLAYER_RADIUS;
  out.maxX = position.x + PLAYER_RADIUS;
  out.minY = position.y;
  out.maxY = position.y + height;
  out.minZ = position.z - PLAYER_RADIUS;
  out.maxZ = position.z + PLAYER_RADIUS;
  return out;
};

/** Eye position for camera and hitscan origin. */
export const eyePosition = (player: PlayerState, out: Vec3): Vec3 => {
  out.x = player.position.x;
  out.y = player.position.y + playerHeight(player) - 0.15;
  out.z = player.position.z;
  return out;
};

const collidesAt = (world: CollisionWorld, position: Vec3, height: number): boolean => {
  playerAabb(position, height, scratchAabb);
  world.queryBroadphase(scratchAabb, scratchIndices);
  for (const index of scratchIndices) {
    if (aabbOverlaps(scratchAabb, world.boxes[index]!)) return true;
  }
  return false;
};

/**
 * Moves `position` by `delta` on a single axis, stopping at the first blocking
 * face. Returns true if movement was blocked.
 */
const moveAxis = (
  world: CollisionWorld,
  position: Vec3,
  height: number,
  axis: 'x' | 'y' | 'z',
  delta: number,
): boolean => {
  if (delta === 0) return false;
  const start = position[axis];
  position[axis] = start + delta;
  playerAabb(position, height, scratchAabb);
  world.queryBroadphase(scratchAabb, scratchIndices);

  let blocked = false;
  let resolved = position[axis];
  for (const index of scratchIndices) {
    const box = world.boxes[index]!;
    if (!aabbOverlaps(scratchAabb, box)) continue;
    blocked = true;
    if (axis === 'y') {
      // Feet-anchored on Y: min is the feet, max is the head.
      resolved =
        delta > 0
          ? Math.min(resolved, box.minY - height - EPSILON)
          : Math.max(resolved, box.maxY + EPSILON);
    } else {
      const min = axis === 'x' ? box.minX : box.minZ;
      const max = axis === 'x' ? box.maxX : box.maxZ;
      resolved =
        delta > 0
          ? Math.min(resolved, min - PLAYER_RADIUS - EPSILON)
          : Math.max(resolved, max + PLAYER_RADIUS + EPSILON);
    }
  }
  if (blocked) position[axis] = resolved;
  return blocked;
};

/**
 * Sweeps the player through the world, sliding along surfaces and stepping up
 * small ledges. Mutates `position` and zeroes blocked components of `velocity`.
 * Returns whether the player is standing on something.
 */
export const sweepMove = (
  world: CollisionWorld,
  position: Vec3,
  velocity: Vec3,
  height: number,
  dt: number,
): boolean => {
  const dx = velocity.x * dt;
  const dy = velocity.y * dt;
  const dz = velocity.z * dt;

  const maxDelta = Math.max(Math.abs(dx), Math.abs(dy), Math.abs(dz));
  const steps = Math.max(1, Math.ceil(maxDelta / MAX_SUBSTEP));
  const sx = dx / steps;
  const sy = dy / steps;
  const sz = dz / steps;

  let onGround = false;

  for (let step = 0; step < steps; step++) {
    // Vertical first, so ground contact is known before horizontal step-up.
    if (moveAxis(world, position, height, 'y', sy)) {
      if (sy <= 0) onGround = true;
      velocity.y = 0;
    }

    for (const axis of ['x', 'z'] as const) {
      const delta = axis === 'x' ? sx : sz;
      if (delta === 0) continue;
      const before = position[axis];
      if (!moveAxis(world, position, height, axis, delta)) continue;

      // Blocked: try stepping up over a low ledge, otherwise slide along it.
      const blockedAt = position[axis];
      const stepY = position.y;
      position[axis] = before;
      position.y = stepY + STEP_HEIGHT;
      if (
        !collidesAt(world, position, height) &&
        !moveAxis(world, position, height, axis, delta)
      ) {
        // Settle back down onto the ledge.
        moveAxis(world, position, height, 'y', -STEP_HEIGHT);
        onGround = true;
      } else {
        position.y = stepY;
        position[axis] = blockedAt;
        velocity[axis] = 0;
      }
    }
  }

  if (!onGround) {
    // Probe just below the feet so walking off a ledge doesn't flicker.
    const probeY = position.y;
    position.y -= EPSILON * 4;
    onGround = collidesAt(world, position, height);
    position.y = probeY;
  }

  return onGround;
};

/** Quake-style directional acceleration: only adds speed along `dir` up to `maxSpeed`. */
const accelerate = (
  velocity: Vec3,
  dirX: number,
  dirZ: number,
  maxSpeed: number,
  accel: number,
  dt: number,
): void => {
  const projected = velocity.x * dirX + velocity.z * dirZ;
  const addSpeed = maxSpeed - projected;
  if (addSpeed <= 0) return;
  const accelSpeed = Math.min(accel * maxSpeed * dt, addSpeed);
  velocity.x += dirX * accelSpeed;
  velocity.z += dirZ * accelSpeed;
};

const applyFriction = (velocity: Vec3, friction: number, dt: number): void => {
  const speed = Math.hypot(velocity.x, velocity.z);
  if (speed < 0.01) {
    velocity.x = 0;
    velocity.z = 0;
    return;
  }
  const drop = Math.max(speed, GROUND_MAX_SPEED * 0.25) * friction * dt;
  const scale = Math.max(0, speed - drop) / speed;
  velocity.x *= scale;
  velocity.z *= scale;
};

/**
 * Advances one player by exactly one tick. This function is the heart of the
 * netcode: the client runs it to predict, the server runs it to be authoritative,
 * and the client re-runs it during reconciliation. It must be pure with respect
 * to (player, input, world, dt) — no wall-clock time, no randomness.
 */
export const stepMovement = (
  world: CollisionWorld,
  player: PlayerState,
  input: InputCommand,
  dt: number,
): void => {
  const dtMs = dt * 1000;

  player.yaw = input.yaw;
  player.pitch = clamp(input.pitch, -MAX_PITCH, MAX_PITCH);

  if (!player.alive) {
    player.velocity.x = 0;
    player.velocity.y = 0;
    player.velocity.z = 0;
    return;
  }

  const wantJump = hasButton(input.buttons, Button.Jump);
  const wantCrouch = hasButton(input.buttons, Button.Crouch);
  const wantSprint = hasButton(input.buttons, Button.Sprint);

  // --- Intent ---------------------------------------------------------------
  const { forward, right } = yawBasis(player.yaw);
  let wishX = 0;
  let wishZ = 0;
  if (hasButton(input.buttons, Button.Forward)) {
    wishX += forward.x;
    wishZ += forward.z;
  }
  if (hasButton(input.buttons, Button.Back)) {
    wishX -= forward.x;
    wishZ -= forward.z;
  }
  if (hasButton(input.buttons, Button.Right)) {
    wishX += right.x;
    wishZ += right.z;
  }
  if (hasButton(input.buttons, Button.Left)) {
    wishX -= right.x;
    wishZ -= right.z;
  }
  const wishLength = Math.hypot(wishX, wishZ);
  if (wishLength > 1e-6) {
    wishX /= wishLength;
    wishZ /= wishLength;
  }

  // --- Timers ---------------------------------------------------------------
  player.slideCooldownMs = Math.max(0, player.slideCooldownMs - dtMs);
  player.coyoteMs = player.onGround
    ? COYOTE_TIME_MS
    : Math.max(0, player.coyoteMs - dtMs);
  if (wantJump && !player.wasJumpDown) player.jumpBufferMs = JUMP_BUFFER_MS;
  else player.jumpBufferMs = Math.max(0, player.jumpBufferMs - dtMs);
  player.wasJumpDown = wantJump;

  const crouchPressed = wantCrouch && !player.wasCrouchDown;
  player.wasCrouchDown = wantCrouch;

  const horizontalSpeed = Math.hypot(player.velocity.x, player.velocity.z);

  // --- Slide ----------------------------------------------------------------
  if (player.sliding) {
    player.slideTimerMs -= dtMs;
    // A slide is a commitment: the press starts it and it runs its full
    // duration whether or not crouch stays held. The only outs are jumping
    // (handled in the jump block), being physically stopped, and a real fall —
    // a single airborne tick from skimming a pad lip or a step edge at slide
    // speed must not cancel it, so airborne time gets a grace window.
    player.slideAirMs = player.onGround ? 0 : player.slideAirMs + dtMs;
    if (
      player.slideTimerMs <= 0 ||
      player.slideAirMs > SLIDE_AIR_GRACE_MS ||
      horizontalSpeed < SLIDE_MIN_SPEED * 0.5
    ) {
      player.sliding = false;
      player.slideCooldownMs = SLIDE_COOLDOWN_MS;
    }
  } else if (
    wantCrouch &&
    player.onGround &&
    player.slideCooldownMs <= 0 &&
    // Crouch while moving means slide, always: a *fresh* press with movement
    // input slides no matter how slow you are, and holding crouch through a
    // fast landing (bhop chains) still re-slides via the speed gate. Only a
    // stationary press falls through to the actual crouch. The press-edge
    // requirement is what keeps held-C from chain-sliding out of a crouch-walk.
    (horizontalSpeed >= SLIDE_MIN_SPEED || (crouchPressed && wishLength > 1e-6))
  ) {
    player.sliding = true;
    player.slideTimerMs = SLIDE_DURATION_MS;
    player.slideAirMs = 0;
    // Launch along current motion — or along the stick when starting from a
    // near-standstill — floored at SLIDE_MIN_SPEED so a slow entry still
    // produces a real slide instead of one that dies on the next tick.
    const dirX = horizontalSpeed > 1e-3 ? player.velocity.x / horizontalSpeed : wishX;
    const dirZ = horizontalSpeed > 1e-3 ? player.velocity.z / horizontalSpeed : wishZ;
    // Capped so slide-jump-reslide chains can't stack the impulse into
    // unbounded speed — but never slower than the player already moves.
    const launch = Math.max(
      horizontalSpeed,
      Math.min(Math.max(horizontalSpeed, SLIDE_MIN_SPEED) + SLIDE_IMPULSE, SLIDE_MAX_LAUNCH),
    );
    player.velocity.x = dirX * launch;
    player.velocity.z = dirZ * launch;
  }

  // Crouching requires headroom to stand back up.
  const wantsShort = wantCrouch || player.sliding;
  if (wantsShort) {
    player.crouching = !player.sliding;
  } else if (player.crouching) {
    player.crouching = collidesAt(world, player.position, PLAYER_HEIGHT);
  }

  const height = playerHeight(player);

  // --- Acceleration ---------------------------------------------------------
  if (player.onGround) {
    if (player.sliding) {
      applyFriction(player.velocity, SLIDE_FRICTION, dt);
      // Slight steering while sliding, no acceleration.
      accelerate(player.velocity, wishX, wishZ, AIR_MAX_SPEED, AIR_ACCEL * 0.5, dt);
    } else {
      applyFriction(player.velocity, GROUND_FRICTION, dt);
      const maxSpeed = player.crouching
        ? CROUCH_MAX_SPEED
        : wantSprint
          ? SPRINT_MAX_SPEED
          : GROUND_MAX_SPEED;
      accelerate(player.velocity, wishX, wishZ, maxSpeed, GROUND_ACCEL, dt);
    }
  } else {
    accelerate(player.velocity, wishX, wishZ, AIR_MAX_SPEED, AIR_ACCEL, dt);
  }

  // --- Jump -----------------------------------------------------------------
  if (player.jumpBufferMs > 0 && player.coyoteMs > 0) {
    player.velocity.y = JUMP_SPEED;
    player.onGround = false;
    player.coyoteMs = 0;
    player.jumpBufferMs = 0;
    if (player.sliding) {
      player.sliding = false;
      player.slideCooldownMs = SLIDE_COOLDOWN_MS;
    }
  }

  // --- Integrate ------------------------------------------------------------
  player.velocity.y -= GRAVITY * dt;
  if (player.velocity.y < -60) player.velocity.y = -60;

  player.onGround = sweepMove(world, player.position, player.velocity, height, dt);
  if (player.onGround && player.velocity.y < 0) player.velocity.y = 0;
};
